import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, FolderOpen, X, Briefcase, User, Users, History, Eye, PenLine, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import BotaoFechar, { useEscFecha } from '../components/BotaoFechar';
import { formatDateTimeBR, formatBRL } from '../lib/format';
import { useProject } from '../context/ProjectContext';

/* Os papéis são uma escada: dono > editor > leitor. Ver o comentário
   equivalente no backend, em PARTICIPANTES DE PROJETO. */
const PAPEL = {
  dono:   { rotulo: 'Dono',   Icone: Crown,    cor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  editor: { rotulo: 'Editor', Icone: PenLine,  cor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  leitor: { rotulo: 'Leitor', Icone: Eye,      cor: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
};

function Selo({ papel }) {
  const p = PAPEL[papel] || PAPEL.leitor;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${p.cor}`}>
      <p.Icone className="w-3 h-3" /> {p.rotulo}
    </span>
  );
}

export default function ProjectsManagement() {
  const { projects, refresh, currentProject, setCurrentProject } = useProject();
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [participantes, setParticipantes] = useState(null);   // projeto
  const [historico, setHistorico] = useState(null);           // projeto

  const save = async (data) => {
    if (editing) await api.put(`/projects/${editing.id}`, data);
    else await api.post('/projects', data);
    setShowForm(false); setEditing(null);
    await refresh();
  };
  const remove = async (p) => {
    if (!window.confirm(`Excluir projeto "${p.name}"? Todos os dados serão perdidos.`)) return;
    await api.delete(`/projects/${p.id}`);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">Gerenciar Projetos</h2>
          <p className="text-slate-500 dark:text-slate-400">Crie e organize seus projetos financeiros</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md">
          <Plus className="w-4 h-4" /> Novo Projeto
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => {
          const isActive = currentProject?.id === p.id;
          const ehDono = (p.papel || 'dono') === 'dono';
          const Icon = p.type === 'business' ? Briefcase : User;
          return (
            <div key={p.id} className={`bg-white dark:bg-slate-800 rounded-2xl border-2 ${isActive ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 dark:border-slate-700'} shadow-sm p-5`}>
              <div className="flex items-start justify-between mb-3">
                {p.logo_url ? (
                  <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl object-cover border border-slate-200 dark:border-slate-700" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-white" />
                  </div>
                )}
                {/* Só o dono edita ou apaga o projeto — o backend recusaria
                    de qualquer forma, mas oferecer o botão e negar depois é
                    uma promessa quebrada. */}
                {ehDono && (
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate">{p.name}</h3>
                <Selo papel={p.papel || 'dono'} />
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mt-1"><Icon className="w-3 h-3" />{p.type === 'business' ? 'Empresa' : 'Pessoal'}</div>
              {p.description && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">{p.description}</p>}
              <button
                onClick={() => setCurrentProject(p)}
                disabled={isActive}
                className={`w-full mt-4 px-3 py-2 rounded-lg text-sm font-medium ${isActive ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 cursor-default' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
              >{isActive ? 'Projeto Ativo' : 'Ativar'}</button>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setParticipantes(p)} data-testid={`projeto-membros-${p.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <Users className="w-3.5 h-3.5" /> Participantes
                </button>
                {ehDono && (
                  <button onClick={() => setHistorico(p)} data-testid={`projeto-historico-${p.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40">
                    <History className="w-3.5 h-3.5" /> Histórico
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && <ProjectForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />}
      {participantes && <ParticipantesModal projeto={participantes} onClose={() => setParticipantes(null)} />}
      {historico && <HistoricoModal projeto={historico} onClose={() => setHistorico(null)} />}
    </div>
  );
}

/**
 * Quem alcança este projeto, e com que papel.
 *
 * Só o dono altera a lista. Qualquer participante pode ver quem são os outros:
 * saber com quem se divide um caderno de contas não é privilégio de ninguém.
 */
function ParticipantesModal({ projeto, onClose }) {
  useEscFecha(onClose);
  const [dados, setDados] = useState(null);
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('editor');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(
    () => api.get(`/projects/${projeto.id}/membros`).then((r) => setDados(r.data)),
    [projeto.id],
  );
  useEffect(() => { carregar(); }, [carregar]);

  const ehDono = dados?.seu_papel === 'dono';

  const adicionar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const { data } = await api.post(`/projects/${projeto.id}/membros`, { email, role: papel });
      setDados((d) => ({ ...d, membros: data.membros }));
      setEmail('');
      toast.success('Participante adicionado');
    } catch (err) {
      toast.error('Não foi possível adicionar', { description: err?.response?.data?.detail || err.message });
    } finally { setSalvando(false); }
  };

  const remover = async (m) => {
    try {
      const { data } = await api.delete(`/projects/${projeto.id}/membros/${m.user_id}`);
      setDados((d) => ({ ...d, membros: data.membros }));
      toast.success('Participante removido');
    } catch (err) {
      toast.error('Não foi possível remover', { description: err?.response?.data?.detail || err.message });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="modal-participantes">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Participantes</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{projeto.name}</p>
          </div>
          <BotaoFechar onClose={onClose} testid="participantes-fechar" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!dados && <div className="text-slate-500 dark:text-slate-400 text-sm">Carregando...</div>}

          {dados?.membros?.map((m) => (
            <div key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{m.name || m.email}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <Selo papel={m.role} />
                {ehDono && m.removivel && (
                  <button onClick={() => remover(m)} data-testid={`remover-membro-${m.user_id}`}
                    className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" title="Remover do projeto">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {ehDono && (
            <form onSubmit={adicionar} className="pt-2 space-y-2">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Adicionar alguém</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="e-mail de quem já usa o sistema" data-testid="membro-email"
                  className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg text-sm" />
                <select value={papel} onChange={(e) => setPapel(e.target.value)} data-testid="membro-papel"
                  className="px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg text-sm">
                  <option value="editor">Editor</option>
                  <option value="leitor">Leitor</option>
                </select>
                <button type="submit" disabled={salvando} data-testid="membro-adicionar"
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-500 text-white text-sm font-medium disabled:opacity-50">
                  {salvando ? '...' : 'Adicionar'}
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                A pessoa precisa já ter conta. Para criar uma, use <strong>Admin → Convidar usuário</strong>.
              </p>
            </form>
          )}

          {dados && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-3 space-y-1">
              {Object.entries(dados.papeis || {}).map(([id, desc]) => (
                <div key={id} className="flex items-start gap-2 text-xs">
                  <Selo papel={id} />
                  <span className="text-slate-600 dark:text-slate-400 pt-0.5">{desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Quem fez o quê, quando, e o que mudou.
 *
 * Só o dono vê. É o que torna aceitável, mais adiante, o agente gravar
 * sozinho — escrita sem rastro em sistema financeiro não se audita.
 */
function HistoricoModal({ projeto, onClose }) {
  useEscFecha(onClose);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get(`/projects/${projeto.id}/historico`, { params: { limit: 200 } })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(e?.response?.data?.detail || 'Não foi possível carregar.'));
  }, [projeto.id]);

  const ACAO = {
    criou:   { rotulo: 'criou',   cor: 'text-emerald-600 dark:text-emerald-400' },
    alterou: { rotulo: 'alterou', cor: 'text-amber-600 dark:text-amber-400' },
    excluiu: { rotulo: 'excluiu', cor: 'text-rose-600 dark:text-rose-400' },
  };
  const COLECAO = {
    transactions: 'lançamento', pots: 'pote', accounts: 'conta', tags: 'tag',
    entities: 'pessoa', revenue_sources: 'fonte', monthly_distributions: 'distribuição',
    accounts_payable_receivable: 'conta a pagar/receber', project_members: 'participante',
  };
  const valor = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') return formatBRL(v);
    return String(v);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="modal-historico">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Histórico de alterações</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {projeto.name}{dados ? ` · ${dados.total} registro(s)` : ''}
            </p>
          </div>
          <BotaoFechar onClose={onClose} testid="historico-fechar" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {erro && <div className="text-sm text-rose-600 dark:text-rose-400">{erro}</div>}
          {!dados && !erro && <div className="text-slate-500 dark:text-slate-400 text-sm">Carregando...</div>}
          {dados?.items?.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Nada registrado ainda. O histórico começa a partir da Etapa D3 —
              o que veio antes não tem autoria gravada, e inventá-la seria pior que não tê-la.
            </div>
          )}
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {dados?.items?.map((it, i) => {
              const a = ACAO[it.acao] || { rotulo: it.acao, cor: 'text-slate-600' };
              const mudancas = Object.entries(it.alteracoes || {});
              return (
                <div key={i} className="py-3 text-sm">
                  <div className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className="font-medium text-slate-800 dark:text-slate-100">{it.email || it.user_id}</span>
                    <span className={`font-medium ${a.cor}`}>{a.rotulo}</span>
                    <span className="text-slate-600 dark:text-slate-400">
                      {COLECAO[it.colecao] || it.colecao}
                      {it.item_nome ? ` "${it.item_nome}"` : ` #${it.item_id}`}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto whitespace-nowrap">
                      {formatDateTimeBR(it.quando)}
                    </span>
                  </div>
                  {mudancas.length > 0 && (
                    <div className="mt-1.5 ml-1 pl-3 border-l-2 border-slate-200 dark:border-slate-600 space-y-0.5">
                      {mudancas.map(([campo, m]) => (
                        <div key={campo} className="text-xs text-slate-600 dark:text-slate-400">
                          <span className="text-slate-500 dark:text-slate-500">{campo}:</span>{' '}
                          <span className="line-through opacity-70">{valor(m.de)}</span>
                          {' → '}
                          <span className="font-medium text-slate-800 dark:text-slate-200">{valor(m.para)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectForm({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || { name: '', type: 'personal', description: '', logo_url: '' });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  useEscFecha(onClose);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{initial ? 'Editar' : 'Novo'} Projeto</h3>
          <BotaoFechar onClose={onClose} testid="projeto-form-fechar" />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3">
          <div><label className="text-sm font-medium text-slate-700 dark:text-slate-300">Nome</label><input value={form.name} onChange={(e)=>upd('name', e.target.value)} required className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg" /></div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tipo</label>
            <div className="flex gap-2 mt-1">
              {['personal','business'].map((t) => (
                <button type="button" key={t} onClick={() => upd('type', t)} className={`flex-1 py-2 rounded-lg font-medium text-sm ${form.type===t?'bg-indigo-600 text-white':'bg-slate-100 text-slate-700 dark:text-slate-300'}`}>
                  {t==='personal'?'Pessoal':'Empresa'}
                </button>
              ))}
            </div>
          </div>
          <div><label className="text-sm font-medium text-slate-700 dark:text-slate-300">Descrição</label><textarea value={form.description||''} onChange={(e)=>upd('description', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg" rows="2" /></div>
          <div><label className="text-sm font-medium text-slate-700 dark:text-slate-300">URL do logo (opcional)</label><input value={form.logo_url||''} onChange={(e)=>upd('logo_url', e.target.value)} placeholder="https://..." className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg" /></div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40">Cancelar</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
